// ============================================================
// Deploy's gestures: plant, formation, sweep, replace, drop
// ============================================================
// The steps sceneGestures.js runs in Deploy mode, each answering "was this
// mine?" for one press, move or release (see its header). Deploy owns the
// right button - recall and the sweep - exactly as Build owns it for erase.

import { sweepBox, isSweep, sweptIds, areaCapacity } from '../../engine/battlefield/deployment';

/** The `data-token-id` of the marker an element belongs to, or undefined. */
export const markerIdOf = (el) => el?.closest?.('[data-token-id]')?.getAttribute('data-token-id');

/**
 * The combatant whose marker is under a drag event, or null.
 *
 * `elementFromPoint` rather than footprint arithmetic, for the reason
 * right-click-to-recall already gives: the marker's `<g>` is what the GM is
 * looking at, and its hit area is the picture's, not a circle's.
 */
function tokenUnder(e) {
  const under = document.elementFromPoint(e.clientX, e.clientY);
  return under?.closest?.('[data-token-id]')?.getAttribute('data-token-id') ?? null;
}

/**
 * THE SWEEP. Right-drag gathers everyone inside the box back into the
 * active room; shift+right is still the camera, and a press that does not
 * travel is left alone, so right-click-to-recall on one marker still works.
 *
 * NO `preventDefault` HERE. Chromium raises `contextmenu` off the
 * right-button press, and preventing the press suppresses it - which
 * silently killed right-click-to-recall (v8.24.0).
 */
export function sweepDown(ctx, e) {
  if (!ctx.deploying || e.button !== 2 || e.shiftKey) return false;
  ctx.sweep.current = { id: e.pointerId, x: e.clientX, y: e.clientY };
  e.currentTarget.setPointerCapture?.(e.pointerId);
  return true;
}

/**
 * ARMED: a marker under the press is the departed. Ground still plants, so
 * an armed bar costs nothing until you actually click somebody (v8.344.0).
 */
export function replacePickDown(ctx, e) {
  if (!ctx.deploying || !ctx.replaceArmed || e.button !== 0) return false;
  const picked = markerIdOf(e.target);
  if (!picked) return false;
  e.preventDefault();
  ctx.setReplaceArmed(false);
  ctx.onReplacePick?.(picked);
  return true;
}

/**
 * A left click plants the marker or moves it - nothing else in Deploy takes
 * a press. THE FLAG PLANTS ON THE PRESS, and THE FORMATION DRAG ALWAYS
 * STARTS: gating it on a room with people in it made a drag with an empty
 * room do nothing and say nothing (v8.26.0). It draws, counts zero, and the
 * bar says why.
 */
export function plantDown(ctx, e) {
  if (!ctx.deploying) return false;
  if (e.button !== 0) return true;
  e.preventDefault();
  const from = ctx.deployment_.plantAt(e);
  if (from) ctx.formation.current = { id: e.pointerId, from, sx: e.clientX, sy: e.clientY };
  return true;
}

export function sweepMove(ctx, e) {
  if (ctx.sweep.current?.id !== e.pointerId) return false;
  const box = sweepBox(ctx.sweep.current, { x: e.clientX, y: e.clientY });
  ctx.setSweepBoxState(isSweep(box) ? box : null);
  return true;
}

/** How many of the room a formation from `from` to `to` places. */
function formationCount(from, to, area, ctx) {
  const tile = ctx.tile;
  const fits = area ? areaCapacity(from, to, tile) : Math.floor(Math.hypot(to.x - from.x, to.z - from.z) / tile) + 1;
  return Math.min(ctx.activeRoom?.entries.length ?? 0, Math.max(area ? 1 : 2, fits));
}

/**
 * THE FORMATION LINE, while the button is still down. Six pixels of slop,
 * the tape's: below it a press is a click, and a click just plants the
 * flag. The setting decides line or block; ALT INVERTS IT for this drag,
 * read live, so letting the key go mid-drag turns it back and you watch it.
 */
export function formationMove(ctx, e) {
  const f = ctx.formation.current;
  if (f?.id !== e.pointerId) return;
  if (!(Math.hypot(e.clientX - f.sx, e.clientY - f.sy) > 6)) return;
  const to = ctx.deployment_.groundPoint(e, f.from.y || 0);
  if (!to) return;
  const area = ctx.formationShape === 'block' ? !e.altKey : e.altKey;
  ctx.setFormationLine({ from: f.from, to, area, count: formationCount(f.from, to, area, ctx) });
}

/** Where every marker's foot is on screen, for the sweep's box to catch. */
function markerFeet(sceneEl) {
  return [...(sceneEl?.querySelectorAll('[data-testid^="token-foot-"]') ?? [])].map((node) => {
    const r = node.getBoundingClientRect();
    return {
      id: node.getAttribute('data-testid').replace('token-foot-', ''),
      x: r.x + r.width / 2,
      y: r.y + r.height / 2,
    };
  });
}

/**
 * The sweep lands: everyone inside the box goes back to the room. A press
 * that never travelled is a right-CLICK, and the context-menu handler owns
 * that - so it falls through.
 */
export function sweepUp(ctx, e) {
  if (ctx.sweep.current?.id !== e.pointerId) return false;
  const start = ctx.sweep.current;
  ctx.sweep.current = null;
  e.currentTarget.releasePointerCapture?.(e.pointerId);
  const box = sweepBox(start, { x: e.clientX, y: e.clientY });
  ctx.setSweepBoxState(null);
  if (!isSweep(box)) return false;
  const caught = sweptIds(markerFeet(ctx.sceneEl.current), box);
  if (caught.length) ctx.onRecallMany?.(caught);
  return true;
}

/**
 * THE FORMATION LANDS ON THE RELEASE. The line is cleared whether or not it
 * dropped anybody, so an abandoned drag never leaves a ghost line lying on
 * the map.
 */
export function formationUp(ctx, e) {
  if (ctx.formation.current?.id !== e.pointerId) return false;
  ctx.formation.current = null;
  const line = ctx.formationLine;
  if (line && ctx.activeRoomId) {
    ctx.arrivalPlan.current = { from: line.from, to: line.to, area: line.area };
    ctx.onDeployRoom?.(ctx.activeRoomId, line.count);
    ctx.setFormationLine(null);
    return true;
  }
  ctx.setFormationLine(null);
  return false;
}

/**
 * DROPPING A NAME OUT OF THE ROOM. HTML drag is a separate event stream
 * from pointers, which is why it was chosen: nothing here can swallow the
 * click that plants the marker (v8.22.0). A drop ON SOMEBODY replaces
 * them on their own spot (v8.343.0), so the marker under the drag wears
 * the hover ring meanwhile.
 */
export function dragOver(ctx, e) {
  e.preventDefault();
  e.dataTransfer.dropEffect = 'move';
  const over = ctx.onReplaceMember ? tokenUnder(e) : null;
  ctx.setDropOverId((cur) => (cur === over ? cur : over));
}

export function drop(ctx, e) {
  e.preventDefault();
  ctx.setDropOverId(null);
  const id = e.dataTransfer.getData('text/plain');
  if (!id) return;
  const outgoing = ctx.onReplaceMember ? tokenUnder(e) : null;
  if (outgoing && outgoing !== id) {
    ctx.onReplaceMember(id, outgoing);
    return;
  }
  const at = ctx.deployment_.groundPoint(e);
  if (!at) return;
  ctx.arrivalPlan.current = { at };
  ctx.onDeployMember?.(id);
}
